import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { usePackageStoreNew } from '../state/packages.store'
import { PackagesMap } from '@geartrack/shared-lib/dist/PackageModel'
import { useAuth } from './AuthProvider'
import { sortedPackagesArray } from '../pages/PackagesPage/PackageTimelines/order-packages'
import usePackagesRepository from '../hooks/usePackagesRepository'
import { notifyAndLog } from '../libraries/utils'
import UpdateMomentLocaleHoC from './UpdateMomentLocaleHoC'

interface IPackagesContext {
  packages: PackagesMap
}

const PackagesContext = createContext<IPackagesContext>({ packages: {} })

const PackagesProvider = props => {
  const { user } = useAuth()
  const localPackages = usePackageStoreNew(state => state.packages)

  const [packages, setPackages] = useState<PackagesMap>(() => {
    if (user?.packages) {
      return user.packages
    }

    return localPackages
  })

  // listen for remote packages updates
  useEffect(() => {
    if (user?.packages) {
      setPackages(user.packages)
    }
  }, [user?.packages])

  // listen for local packages updates, set if user is not signed in
  useEffect(() => {
    if (!user?.id) {
      setPackages(localPackages)
    }
  }, [localPackages, user?.id])

  const contextValues: IPackagesContext = useMemo(() => ({ packages }), [packages])

  return <PackagesContext.Provider value={contextValues}>{props.children}</PackagesContext.Provider>
}

/**
 * Because of bugs, sometimes we may have pkgs with the same key
 * to prevent client side errors, we try to remove them here
 * @param props
 * @constructor
 */
const RemoveDuplicatedComputedKeys = props => {
  const { packages } = useContext<IPackagesContext>(PackagesContext)
  const pkgRepo = usePackagesRepository()

  useEffect(() => {
    const existing = {}
    const duplicates: PackagesMap = {}

    for (let packagesKey in packages) {
      const pkg = packages[packagesKey]

      if (existing[pkg.computedKey]) {
        duplicates[pkg.computedKey] = pkg
      } else {
        existing[pkg.computedKey] = pkg
      }

      // also remove packages where the map key does not match computed key
      if (packagesKey !== pkg.computedKey) {
        duplicates[packagesKey] = pkg
      }
    }

    const duplicatedEntries = Object.values(duplicates)

    // Remove duplicates from repository
    if (duplicatedEntries.length > 0) {
      for (let duplicatedPackage of duplicatedEntries) {
        pkgRepo.removePackage(duplicatedPackage)
      }

      notifyAndLog('Packages with duplicated computedKeys found', {
        pkgs: duplicatedEntries,
      })
    }
  }, [packages, pkgRepo])

  return props.children
}

export const usePackages = () => {
  const { packages } = useContext<IPackagesContext>(PackagesContext)

  return useMemo(() => {
    return sortedPackagesArray(packages)
  }, [packages])
}

export const withPackages = Component => {
  return () => {
    return (
      <UpdateMomentLocaleHoC>
        <PackagesProvider>
          <RemoveDuplicatedComputedKeys>
            <Component />
          </RemoveDuplicatedComputedKeys>
        </PackagesProvider>
      </UpdateMomentLocaleHoC>
    )
  }
}

export default PackagesProvider
